import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import type { ColumbusPlannerTermModel } from '@/api/generated/schemas';
import type { TermPlan } from '../../columbusTypes';
import { expandTermPersons } from '../../columbusPlans';

interface Props {
  term: ColumbusPlannerTermModel;
  plan: TermPlan;
}

export function PersonList({ term, plan }: Props) {
  const { t } = useTranslation('columbus-planner');
  const rows = useMemo(() => Array.from(expandTermPersons(term, plan)), [term, plan]);
  if (rows.length === 0) return null;

  const passengers = term.passengers ?? [];

  return (
    <table className="text-xs whitespace-nowrap">
      <thead>
        <tr className="text-right">
          <th className="text-phx-text-muted px-2 py-0.5 text-left font-semibold"></th>
          <th className="text-phx-text-muted px-2 py-0.5 pr-0 text-left font-semibold">
            {t('personList.person')}
          </th>
          <th className="text-phx-text-muted px-2 py-0.5 pl-0 font-semibold"></th>
          <th className="text-phx-text-muted px-2 py-0.5 text-left font-semibold">
            {t('personList.dep')}
          </th>
          <th className="text-phx-text-muted px-2 py-0.5 text-left font-semibold">
            {t('personList.ret')}
          </th>
          <th className="text-phx-text-muted px-2 py-0.5 font-semibold">
            {t('personList.tariff')}
          </th>
          <th
            className="text-phx-text-muted px-2 py-0.5 font-semibold"
            title={t('personList.expenseTitle')}
          >
            {t('personList.expense')}
          </th>
          <th
            className="text-phx-text-muted px-2 py-0.5 font-semibold"
            title={t('personList.incomeTitle')}
          >
            {t('personList.income')}
          </th>
          <th
            className="text-phx-text-muted px-2 py-0.5 font-semibold"
            title={t('personList.totalExpenseTitle')}
          >
            {t('personList.totalExpense')}
          </th>
          <th
            className="text-phx-text-muted px-2 py-0.5 font-semibold"
            title={t('personList.totalIncomeTitle')}
          >
            {t('personList.totalIncome')}
          </th>
          <th className="text-phx-text-muted px-2 py-0.5 font-semibold">
            {t('personList.profit')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, pIdx) => {
          const person = passengers[pIdx];
          const rowProfit = r.totalIncome - r.totalExpense;
          const profitPositive = rowProfit > 0;

          return (
            <tr
              key={pIdx}
              className={cn(
                'border-t-phx-border border-t border-solid text-right whitespace-nowrap',
                // Zebra proužkování skupiny cestujícího. Tinty jsou mode-aware, takže
                // odpadají ruční dark: páry, které tu byly kvůli holé indigo paletě.
                pIdx % 2 === 0 ? 'bg-phx-primary-tint' : 'bg-phx-surface-2'
              )}
            >
              {/* Profit indicator cell */}
              <td
                className={cn(
                  'border-t border-solid px-2 py-px text-left',
                  profitPositive
                    ? 'border-t-phx-success bg-phx-success-tint'
                    : 'border-t-phx-danger bg-phx-danger-tint'
                )}
              >
                {pIdx + 1}
              </td>
              <td className="px-2 py-px pr-0">{person?.name ?? '-'}</td>
              <td className="px-2 py-px pl-0">
                {person?.isReservation && (
                  <span
                    className="text-phx-danger ml-1 font-bold"
                    title={t('personList.justReservation')}
                  >
                    R
                  </span>
                )}
              </td>
              <td className="px-2 py-px text-center">
                <PersonTicketCell
                  cost={r.depTicket?.cost ?? null}
                  code={r.depTicket?.code ?? null}
                  isUnassigned={r.depTicket?.isUnassigned}
                  isPerson={!!person}
                />
              </td>
              <td className="px-2 py-px text-center">
                <PersonTicketCell
                  cost={r.retTicket?.cost ?? null}
                  code={r.retTicket?.code ?? null}
                  isUnassigned={r.retTicket?.isUnassigned}
                  isPerson={!!person}
                />
              </td>
              <td className="px-2 py-px">{r.tariff + 1}</td>
              <td className="px-2 py-px">
                {!person && r.minBreakingRetail != null
                  ? `(${r.minBreakingRetail})`
                  : (r.expense ?? '')}
              </td>
              <td className="px-2 py-px">{r.retail}</td>
              <td className="px-2 py-px">{r.totalExpense}</td>
              <td className="px-2 py-px">{r.totalIncome}</td>
              <td className="px-2 py-px font-semibold">{rowProfit}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PersonTicketCell({
  cost,
  code,
  isUnassigned,
  isPerson,
}: {
  cost: number | null;
  code: string | null;
  isUnassigned?: boolean;
  isPerson: boolean;
}) {
  const { t } = useTranslation('columbus-planner');
  if (cost === null) return <span>-</span>;
  return (
    <span title={code ?? undefined}>
      {cost}
      {isUnassigned && isPerson && (
        <span className="text-phx-danger ml-1 text-xs" title={t('personList.notBinded')}>
          ⚠
        </span>
      )}
    </span>
  );
}
